// Where the host's events go on the virtual axis.
//
// The firmware thread never ticks the virtual clock: a host command's `dma_issue.at`
// is 0, a host flag write's `at` is 0, and doorbells, commands, completions and
// interrupts carry only wall-clock nanoseconds. What the host *did* to the cores is
// stamped, though, in the cores' time: the DMA it asked for landed at `dma.end`, the
// flag it wrote released a wait at `wait.t1`, the program it launched started at
// `dispatch.t0`. Each of those pairs the command's wall time with a virtual time, and
// between the pairs wall time is stretched linearly. Every placement remembers whether
// it was exact, interpolated, or clamped to an end, and the rows draw it accordingly.

export function placeHost(model, links) {
  const byChip = new Map();
  model.scDispatches = [];
  for (const [chip, events] of model.hostByChip) {
    const anchors = [];
    const tend = model.chipTend.get(chip) || model.tEnd;
    let exact = 0;

    // Format 2: the simulator placed its firmware commands itself (docs/trace.md,
    // "Where the host is"); those are exact, and so are the flag writes and DMA
    // completions, which carry wall time too. Doorbells and interrupts carry only wall
    // time and are interpolated between them.
    if (model.hostTimed) {
      for (const h of events) {
        if (Number.isInteger(h.t) && (h.ev === "fw_cmd" || h.ev === "fw_done")) {
          h.ps = h.t; h.placed = 0; exact++;
          if (Number.isInteger(h.wall_ns)) anchors.push({ ns: h.wall_ns, ps: h.t });
        }
      }
      if (events.length) {
        anchors.push({ ns: events[0].wall_ns ?? events[0].t_ns, ps: 0 });
        anchors.push({ ns: events[events.length - 1].wall_ns ?? events[events.length - 1].t_ns, ps: tend });
      }
      anchors.sort((a, b) => a.ns - b.ns || a.ps - b.ps);
      let run = 0;
      for (const a of anchors) { if (a.ps < run) a.ps = run; run = a.ps; }
      let interp = 0, clamped = 0, maxGap = 0;
      for (let i = 1; i < anchors.length; i++) maxGap = Math.max(maxGap, anchors[i].ps - anchors[i - 1].ps);
      for (const h of events) {
        if (h.placed === 0) continue;
        const p = interpolate(anchors, h.wall_ns ?? h.t_ns);
        h.ps = p.ps; h.placed = p.clamped ? 2 : 1;
        if (p.clamped) clamped++; else interp++;
      }
      for (const h of events) {
        if (h.ev === "fw_done" && h.cmd && h.ps < h.cmd.ps) h.ps = h.cmd.ps;
      }
      for (const h of events) {
        if (h.ev === "fw_cmd") {
          h.span = [h.ps, h.done ? Math.max(h.done.ps, h.ps) : h.ps];
          if (h.kind === "run" && h.op === "control_tile" && h.core === "SparseCore") {
            model.scDispatches.push({ chip, ci: h.ci, ps: h.ps, placed: h.placed, tileMask: h.tile_mask, flags: h.flags, host: h });
          }
        }
      }
      byChip.set(chip, { exact, interp, clamped, anchors, maxGap });
      continue;
    }

    // (a) A host DMA: its completion is answered when the bytes landed.
    for (const d of model.dmas) {
      if (d.chip !== chip || d.cmd == null || d.byKind !== "host" || d.open) continue;
      const cmd = model.fwByCmd.get(chip + ":" + d.cmd);
      if (!cmd) continue;
      if (cmd.done) { anchors.push({ ns: cmd.done.t_ns, ps: d.end }); cmd.done.ps = d.end; cmd.done.placed = 0; exact++; }
      if (!Number.isNaN(d.start)) cmd.clampMax = Math.min(cmd.clampMax ?? Infinity, d.start);
    }
    // (b) A host flag write that released a wait: the wait's end is its time.
    for (const w of model.waits) {
      if (w.chip !== chip) continue;
      const r = links.releaserOf(w);
      if (r.how !== "host" || !r.host || r.host.chip !== chip) continue;
      anchors.push({ ns: r.host.t_ns, ps: w.t1 });
      if (r.host.placed !== 0) { r.host.ps = w.t1; r.host.placed = 0; exact++; }
    }
    // (c) A dispatch: the run command precedes the program's first cycle.
    for (const d of model.dispatches) {
      if (d.chip !== chip || d.cmd == null) continue;
      const cmd = model.fwByCmd.get(chip + ":" + d.cmd);
      if (!cmd) continue;
      anchors.push({ ns: cmd.t_ns, ps: d.t0 });
      if (cmd.placed !== 0) { cmd.ps = d.t0; cmd.placed = 0; exact++; }
      if (cmd.done && cmd.done.placed !== 0) { cmd.done.ps = d.t0; cmd.done.placed = 0; }
    }
    // Sentinels: the chip's first host event at 0, its last at the chip's end.
    if (events.length) {
      anchors.push({ ns: events[0].t_ns, ps: 0 });
      anchors.push({ ns: events[events.length - 1].t_ns, ps: tend });
    }
    anchors.sort((a, b) => a.ns - b.ns || a.ps - b.ps);
    // Monotone: virtual time cannot run backwards along wall time.
    let run = 0;
    for (const a of anchors) { if (a.ps < run) a.ps = run; run = a.ps; }
    // Collapse equal wall times to the later virtual time.
    const uniq = [];
    for (const a of anchors) {
      if (uniq.length && uniq[uniq.length - 1].ns === a.ns) uniq[uniq.length - 1].ps = Math.max(uniq[uniq.length - 1].ps, a.ps);
      else uniq.push({ ...a });
    }
    let maxGap = 0;
    for (let i = 1; i < uniq.length; i++) maxGap = Math.max(maxGap, uniq[i].ps - uniq[i - 1].ps);

    let interp = 0, clamped = 0;
    for (const h of events) {
      if (h.placed === 0) continue;
      const p = interpolate(uniq, h.t_ns);
      h.ps = p.ps;
      h.placed = p.clamped ? 2 : 1;
      if (p.clamped) clamped++; else interp++;
    }
    for (const h of events) {
      if (h.clampMax != null && h.ps > h.clampMax) h.ps = h.clampMax;
      if (h.ev === "fw_done" && h.cmd && h.ps < h.cmd.ps) h.ps = h.cmd.ps;
    }
    for (const h of events) {
      if (h.ev === "fw_cmd") {
        h.span = [h.ps, h.done ? Math.max(h.done.ps, h.ps) : h.ps];
        if (h.kind === "run" && h.op === "control_tile" && h.core === "SparseCore") {
          model.scDispatches.push({ chip, ci: h.ci, ps: h.ps, placed: h.placed, tileMask: h.tile_mask, flags: h.flags, host: h });
        }
      }
    }
    byChip.set(chip, { exact, interp, clamped, anchors: uniq, maxGap });
  }
  indexScDispatches(model);
  return { byChip };
}

// Every SparseCore row asks for its own dispatches, and a slice has one row per tile of
// every chip: index them once rather than scanning the list per row and per frame.
function indexScDispatches(model) {
  const byCore = new Map();
  for (const d of model.scDispatches) {
    const k = d.chip + ":" + d.ci;
    let a = byCore.get(k);
    if (!a) byCore.set(k, a = []);
    a.push(d);
  }
  for (const a of byCore.values()) a.sort((x, y) => x.ps - y.ps);
  model.scDispatchesOf = (chip, ci) => byCore.get(chip + ":" + ci) || NONE;
}
const NONE = [];

function interpolate(anchors, ns) {
  if (!anchors.length) return { ps: 0, clamped: true };
  if (ns <= anchors[0].ns) return { ps: anchors[0].ps, clamped: ns < anchors[0].ns };
  const last = anchors[anchors.length - 1];
  if (ns >= last.ns) return { ps: last.ps, clamped: ns > last.ns };
  let lo = 0, hi = anchors.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (anchors[mid].ns <= ns) lo = mid; else hi = mid;
  }
  const a = anchors[lo], b = anchors[hi];
  const f = b.ns === a.ns ? 0 : (ns - a.ns) / (b.ns - a.ns);
  return { ps: a.ps + (b.ps - a.ps) * f, clamped: false };
}
